import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Bot, Maximize2, Minimize2, X, Moon, Sun } from 'lucide-react';
import { ChatClient } from '../data/api/ChatClient';
import { ChatMessage } from '../data/interfaces/chatbot.types';
import '../styles/ChatOverlay.css';

interface ChatOverlayProps {
  onClose: () => void;
}

// Engaging loading messages like Claude
const LOADING_MESSAGES = [
  "Analyzing your request...",
  "Searching through the data...",
  "Crafting your response...",
  "Pulling together the details...",
  "Processing information...",
  "Preparing your answer...",
];

const TypingIndicator = () => {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="typing-indicator-container">
      <div className="typing-indicator">
        <span></span>
        <span></span>
        <span></span>
      </div>
      <span className="typing-message">{LOADING_MESSAGES[messageIndex]}</span>
    </div>
  );
};

// Direct API formatters for chip questions
const ChipFormatters = {
  // Format applicants list
  formatApplicants: (data: any): string => {
    const records = data.records || [];
    const totalCount = data.total_no_of_records || data.total_filtered_records || records.length;

    if (!Array.isArray(records) || records.length === 0) {
      return '<h3>No Applicants Found</h3><p>There are no applicants registered yet.</p>';
    }

    let html = `<h3>Found ${totalCount} Applicants</h3>`;
    html += '<p style="color:#6b7280;margin-bottom:12px">Here are the applicants in your institute:</p>';
    html += '<table><tr><th>Name</th><th>Email</th><th>Status</th></tr>';

    records.slice(0, 10).forEach((user: any) => {
      const firstName = user.first_name || '';
      const lastName = user.last_name || '';
      const name = `${firstName} ${lastName}`.trim() || 'N/A';
      const email = user.email_address || user.email || 'N/A';
      const status = user.status || 'Active';
      html += `<tr><td>${name}</td><td>${email}</td><td>${status}</td></tr>`;
    });

    html += '</table>';

    if (totalCount > 10) {
      html += `<p style="text-align:center;margin-top:12px"><a href="#" onclick="event.preventDefault();" class="chat-nav-link" data-path="/dashboard/learners">View all ${totalCount} applicants →</a></p>`;
    }

    return html;
  },

  // Format upcoming schedules
  formatSchedules: (data: any): string => {
    const records = data.records || [];
    const totalCount = data.total_no_of_records || records.length;

    if (!Array.isArray(records) || records.length === 0) {
      return '<h3>No Upcoming Schedules</h3><p>There are no upcoming interview schedules.</p>';
    }

    let html = `<h3>Found ${totalCount} Upcoming Schedules</h3>`;
    html += '<p style="color:#6b7280;margin-bottom:12px">Here are your upcoming interviews:</p>';
    html += '<table><tr><th>Title</th><th>Date</th><th>Status</th></tr>';

    records.slice(0, 10).forEach((schedule: any) => {
      const title = schedule.session_name || schedule.title || 'N/A';
      const date = schedule.start_datetime || schedule.date || 'N/A';
      const status = schedule.status || 'Upcoming';
      html += `<tr><td>${title}</td><td>${date}</td><td>${status}</td></tr>`;
    });

    html += '</table>';

    if (totalCount > 10) {
      html += `<p style="text-align:center;margin-top:12px"><a href="#" onclick="event.preventDefault();" class="chat-nav-link" data-path="/dashboard/interviews">View all ${totalCount} schedules →</a></p>`;
    }

    return html;
  },

  // Format dashboard data - matches actual API response
  formatDashboard: (data: any): string => {
    let html = '<h3>Dashboard Overview</h3>';
    html += '<p style="color:#6b7280;margin-bottom:16px">Here\'s a summary of your platform:</p>';

    // Actual API response: { total_learners, total_active_schedules, completion_rate, interview_schedules }
    const totalApplicants = data.total_learners || 0;
    const activeSchedules = data.total_active_schedules || 0;
    const completionRate = data.completion_rate || 0;
    const currentMonthSchedules = data.interview_schedules || [];

    html += '<div style="display:grid;grid-template-columns:repeat(2,1fr);gap:12px;margin:16px 0">';
    html += `<div style="background:rgba(16,185,129,0.1);padding:16px;border-radius:12px;text-align:center">
      <div style="font-size:28px;font-weight:600;color:#10b981">${totalApplicants}</div>
      <div style="font-size:12px;color:#6b7280">Total Applicants</div>
    </div>`;
    html += `<div style="background:rgba(99,102,241,0.1);padding:16px;border-radius:12px;text-align:center">
      <div style="font-size:28px;font-weight:600;color:#6366f1">${activeSchedules}</div>
      <div style="font-size:12px;color:#6b7280">Active Schedules</div>
    </div>`;
    html += `<div style="background:rgba(245,158,11,0.1);padding:16px;border-radius:12px;text-align:center">
      <div style="font-size:28px;font-weight:600;color:#f59e0b">${completionRate}%</div>
      <div style="font-size:12px;color:#6b7280">Completion Rate</div>
    </div>`;
    html += `<div style="background:rgba(139,92,246,0.1);padding:16px;border-radius:12px;text-align:center">
      <div style="font-size:28px;font-weight:600;color:#8b5cf6">${currentMonthSchedules.length}</div>
      <div style="font-size:12px;color:#6b7280">This Month</div>
    </div>`;
    html += '</div>';

    // Show this month's schedules if any
    if (currentMonthSchedules.length > 0) {
      html += '<h4 style="margin-top:16px;margin-bottom:8px">This Month\'s Schedules:</h4>';
      html += '<table><tr><th>Title</th><th>Date</th><th>Status</th></tr>';
      currentMonthSchedules.slice(0, 5).forEach((schedule: any) => {
        const title = schedule.session_name || 'N/A';
        const date = schedule.start_datetime || 'N/A';
        const status = schedule.status || 'Scheduled';
        html += `<tr><td>${title}</td><td>${date}</td><td>${status}</td></tr>`;
      });
      html += '</table>';
    }

    return html;
  },

  // Format job searches list
  formatJobSearches: (data: any): string => {
    const searches = data.job_searches || data.records || data || [];

    if (!Array.isArray(searches) || searches.length === 0) {
      return '<h3>No Talent Searches</h3><p>You haven\'t created any talent searches yet.</p>';
    }

    const count = searches.length;

    let html = `<h3>Found ${count} Talent Searches</h3>`;
    html += '<p style="color:#6b7280;margin-bottom:12px">Here are your recent talent searches:</p>';
    html += '<table><tr><th>Title</th><th>Status</th><th>Candidates</th></tr>';

    searches.slice(0, 10).forEach((search: any) => {
      const title = search.job_title || search.title || 'N/A';
      const status = search.status || 'Completed';
      const candidates = search.applicant_count || search.candidates_count || 0;
      html += `<tr><td>${title}</td><td>${status}</td><td>${candidates}</td></tr>`;
    });

    html += '</table>';

    if (count > 10) {
      html += `<p style="text-align:center;margin-top:12px"><a href="#" onclick="event.preventDefault();" class="chat-nav-link" data-path="/dashboard/job-search">View all ${count} searches →</a></p>`;
    }

    return html;
  },

  // Format results list
  formatResults: (data: any): string => {
    const records = data.attendance_record || data.data || data.records || data || [];
    const totalCount = records.length;

    if (!Array.isArray(records) || records.length === 0) {
      return '<h3>No Results Found</h3><p>There are no interview results available yet.</p>';
    }

    let html = `<h3>Latest 5 Interview Results</h3>`;
    html += '<p style="color:#6b7280;margin-bottom:12px">Here is the attendance data for your recent sessions:</p>';
    html += '<table><tr><th>Session</th><th>Date</th><th>Attendance</th></tr>';

    records.slice(0, 5).forEach((item: any) => {
      const title = item.session_name || item.title || 'N/A';
      const date = item.start_datetime || item.date ? new Date(item.start_datetime || item.date).toLocaleDateString() : 'N/A';
      
      const targetCount = Array.isArray(item.target_users) && item.target_users.length > 0
        ? item.target_users.length
        : (typeof item.target_users === 'number' && item.target_users > 0 ? item.target_users : 1);

      const isAttended =
        (Array.isArray(item.attended_users) && item.attended_users.length > 0) ||
        (typeof item.attendance_status === 'string' &&
          item.attendance_status.toLowerCase().includes('attend') &&
          !item.attendance_status.toLowerCase().includes('not'));

      let attendedCount = 0;
      if (isAttended) {
        if (Array.isArray(item.attended_users) && item.attended_users.length > 0) {
          attendedCount = Math.min(item.attended_users.length, targetCount);
        } else {
          attendedCount = targetCount;
        }
      }

      const attendanceText = `${attendedCount} / ${targetCount}`;

      html += `<tr><td>${title}</td><td>${date}</td><td>${attendanceText}</td></tr>`;
    });

    html += '</table>';

    if (totalCount > 5) {
      html += `<p style="text-align:center;margin-top:12px"><a href="#" onclick="event.preventDefault();" class="chat-nav-link" data-path="/dashboard/results">View all ${totalCount} results →</a></p>`;
    }

    return html;
  },
};

const HtmlContent = ({ html, isExpanded, onExpandRequest }: { html: string; isExpanded: boolean; onExpandRequest: () => void }) => {
  const chartRegex = /<chart\s+type=["']bar["']\s+data=(['"])(.*?)\1\s*\/>/g;
  const parts: Array<{ type: string; content?: string; data?: any[] }> = [];
  let lastIndex = 0;
  let match;

  while ((match = chartRegex.exec(html)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'html', content: html.substring(lastIndex, match.index) });
    }
    try {
      const chartData = JSON.parse(match[2]);
      parts.push({ type: 'chart', data: chartData });
    } catch (e) {
      console.error('Failed to parse chart data', e);
    }
    lastIndex = chartRegex.lastIndex;
  }

  if (lastIndex < html.length) {
    parts.push({ type: 'html', content: html.substring(lastIndex) });
  }

  const hasComplexData = html.includes('<table') || parts.some(p => p.type === 'chart');

  if (!isExpanded && hasComplexData) {
    return (
      <div className="chat-expand-prompt" onClick={onExpandRequest}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 16H8V19H10V14H5V16ZM8 8H5V10H10V5H8V8ZM19 16H16V19H14V14H19V16ZM16 8H19V10H14V5H16V8Z" fill="currentColor" />
        </svg>
        <span>This message contains detailed charts or tables. Click to expand and view.</span>
      </div>
    );
  }

  const groupedParts: any[] = [];
  let currentGroup: any[] = [];

  parts.forEach(part => {
    if (part.type === 'chart') {
      currentGroup.push(part);
    } else {
      if (currentGroup.length > 0) {
        groupedParts.push({ type: 'chart-group', charts: currentGroup });
        currentGroup = [];
      }
      groupedParts.push(part);
    }
  });
  if (currentGroup.length > 0) {
    groupedParts.push({ type: 'chart-group', charts: currentGroup });
  }

  return (
    <div>
      {groupedParts.length === 0 ? (
        <div dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        groupedParts.map((group, i) => {
          if (group.type === 'html') {
            return <div key={i} dangerouslySetInnerHTML={{ __html: group.content || '' }} />;
          } else if (group.type === 'chart-group') {
            return (
              <div key={i} className="chart-grid-container">
                {group.charts.map((chart: any, idx: number) => (
                  <div key={idx} className="chart-grid-item">
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={chart.data} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.05)" />
                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#4b5563', fontSize: 12 }} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#4b5563', fontSize: 12 }} />
                        <Tooltip cursor={false} contentStyle={{ background: '#ffffff', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: '8px', color: '#111827', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)' }} itemStyle={{ color: '#059669', fontWeight: 500 }} />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                          {(chart.data || []).map((_: any, index: number) => (
                            <Cell key={`cell-${index}`} fill={index % 2 === 0 ? '#34d399' : '#10b981'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ))}
              </div>
            );
          }
          return null;
        })
      )}
    </div>
  );
};

const Typewriter = ({ text, speed = 15, onComplete, scrollRef }: { text: string; speed?: number; onComplete?: () => void; scrollRef?: React.RefObject<HTMLDivElement | null> }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (index < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText((prev) => prev + text.charAt(index));
        setIndex((prev) => prev + 1);
      }, speed);
      return () => clearTimeout(timeout);
    } else if (onComplete) {
      onComplete();
    }
  }, [index, text, speed, onComplete]);

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [displayedText, scrollRef]);

  const cleanDisplay = displayedText.replace(/<chart[^>]*\/>/g, '');

  return <div dangerouslySetInnerHTML={{ __html: cleanDisplay }} />;
};

const ChatOverlay: React.FC<ChatOverlayProps> = ({ onClose }) => {
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const navigate = useNavigate();

  const [messages, setMessages] = useState<Array<ChatMessage & { isNew?: boolean }>>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [pollingJobSearch, setPollingJobSearch] = useState<string | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const scrollToBottom = useCallback(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Handle navigation links in chat messages
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('chat-nav-link')) {
        e.preventDefault();
        const path = target.getAttribute('data-path');
        if (path) {
          onClose(); // Close chatbot first
          navigate(path); // Then navigate
        }
      }
    };

    const container = chatContainerRef.current;
    if (container) {
      container.addEventListener('click', handleClick);
      return () => container.removeEventListener('click', handleClick);
    }
  }, [onClose, navigate]);

  // Job search polling function
  const pollJobSearchStatus = useCallback(async (searchId: string, loadingMsgId: string) => {
    const maxAttempts = 60; // 60 attempts * 2 seconds = 120 seconds max
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setPollingJobSearch(null);
        return;
      }

      try {
        const response = await ChatClient.directApiCall(`/interview_admin/job_search/${searchId}/status`);
        const status = response.data.status;
        const progressMessage = response.data.progress_message || 'Searching for candidates...';
        const progressPercent = response.data.progress_percent || 0;

        if (status === 'COMPLETE' || status === 'COMPLETED' || status === 'FAILED') {
          setPollingJobSearch(null);
          
          // Get results count
          const results = response.data.results || {};
          const totalResults = results.total_results || 0;
          const message = results.message || response.data.message || '';
          
          // Update the loading message with final result
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === loadingMsgId
                ? { ...msg, content: status === 'FAILED' 
                    ? '<h3>Search Failed</h3><p>Please try again or contact support.</p>'
                    : `<h3>Search Complete!</h3><p>${message || `Found <strong>${totalResults}</strong> candidates.`}</p><p><a href="#" onclick="event.preventDefault();" class="chat-nav-link" data-path="/dashboard/job-search/${searchId}">View talent search results →</a></p>`, 
                  isNew: true }
                : msg
            )
          );
        } else {
          // Still processing - update the loading message with progress
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === loadingMsgId
                ? { ...msg, content: `<div class="chat-progress"><div class="typing-indicator-container"><div class="typing-indicator"><span></span><span></span><span></span></div></div><p>${progressMessage}</p><div class="chat-progress-bar"><div class="chat-progress-fill" style="width:${progressPercent}%"></div></div></div>`, isNew: false }
                : msg
            )
          );
          // Continue polling
          attempts++;
          setTimeout(poll, 2000);
        }
      } catch (error) {
        console.error('Polling error:', error);
        setPollingJobSearch(null);
      }
    };

    poll();
  }, []);

  // Quick action handler - direct API calls for chips (bypass Gemini)
  const handleQuickAction = async (chipType: string) => {
    if (isLoading) return;

    // Map chip type to display text
    const chipTextMap: Record<string, string> = {
      applicants: 'List all applicants',
      schedules: 'Show upcoming schedules',
      dashboard: 'Get dashboard data',
      jobSearches: 'Show talent searches',
      results: 'Show the latest 5 interview results',
    };

    const userMessage: ChatMessage & { isNew?: boolean } = {
      id: Date.now().toString(),
      role: 'user',
      content: chipTextMap[chipType] || chipType,
      timestamp: new Date(),
      isNew: true,
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    const loadingMessage: ChatMessage & { isNew?: boolean } = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isNew: true,
    };
    setMessages((prev) => [...prev, loadingMessage]);

    try {
      let formattedResponse = '';

      // Direct API calls based on chip type
      switch (chipType) {
        case 'applicants': {
          const response = await ChatClient.directApiCall('/interview_admin/users');
          console.log('Applicants API response:', response.data);
          formattedResponse = ChipFormatters.formatApplicants(response.data);
          break;
        }
        case 'schedules': {
          // Use status filter to get only upcoming schedules
          const response = await ChatClient.directApiCall('/interview_admin/schedules', { status: 'Upcoming' });
          console.log('Schedules API response:', response.data);
          formattedResponse = ChipFormatters.formatSchedules(response.data);
          break;
        }
        case 'dashboard': {
          const response = await ChatClient.directApiCall('/interview_admin/dashboard');
          console.log('Dashboard API response:', response.data);
          formattedResponse = ChipFormatters.formatDashboard(response.data);
          break;
        }
        case 'jobSearches': {
          const response = await ChatClient.directApiCall('/interview_admin/job_search');
          console.log('Job searches API response:', response.data);
          formattedResponse = ChipFormatters.formatJobSearches(response.data);
          break;
        }
        case 'results': {
          const response = await ChatClient.directApiCall('/interview_admin/attendance');
          console.log('Results API response:', response.data);
          formattedResponse = ChipFormatters.formatResults(response.data);
          break;
        }
        default:
          formattedResponse = 'I can help you with that. Please type your question.';
      }

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === loadingMessage.id
            ? { ...msg, content: formattedResponse, isNew: true }
            : msg
        )
      );
    } catch (error) {
      console.error('Chip API error:', error);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === loadingMessage.id
            ? { ...msg, content: 'Sorry, I encountered an error fetching the data. Please try again.', isNew: true }
            : msg
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isLoading) return;

    const userMessage: ChatMessage & { isNew?: boolean } = {
      id: Date.now().toString(),
      role: 'user',
      content: trimmedInput,
      timestamp: new Date(),
      isNew: true,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    const loadingMessage: ChatMessage & { isNew?: boolean } = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isNew: true,
    };
    setMessages((prev) => [...prev, loadingMessage]);

    try {
      const response = await ChatClient.sendPrompt({
        prompt: trimmedInput,
        conversation_id: conversationId || undefined,
      });

      if (response.data.conversation_id) {
        setConversationId(response.data.conversation_id);
      }

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === loadingMessage.id
            ? { ...msg, content: response.data.response, isNew: true }
            : msg
        )
      );

      // Check if response contains hidden job search ID marker and start polling
      const jobIdMatch = response.data.response.match(/<!--JOB_SEARCH_ID:([a-f0-9-]+)-->/i);
      if (jobIdMatch) {
        const searchId = jobIdMatch[1];
        // Remove the hidden marker from the displayed message
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === loadingMessage.id
              ? { ...msg, content: 'Talent search created! I\'m now searching for the best candidates...', isNew: true }
              : msg
          )
        );
        // Start polling with the loading message ID to update it
        setPollingJobSearch(searchId);
        pollJobSearchStatus(searchId, loadingMessage.id);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === loadingMessage.id
            ? { ...msg, content: 'Sorry, I encountered an error. Please try again.', isNew: true }
            : msg
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClose = () => {
    setMessages([]);
    setInputValue('');
    setConversationId(null);
    setPollingJobSearch(null);
    onClose();
  };

  const RobotIcon = () => (
    <img src="/assistant.png" alt="Assistant" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
  );

  return (
    <div className="chat-modal-overlay" onClick={handleClose}>
      <div className={`chat-modal-inner ${!isExpanded ? 'minimized' : ''} ${isDarkMode ? 'dark-theme' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="chat-shader-bg"></div>
        <div className="chat-modal-glass-content">
          {/* Header */}
          <header className="chat-modal-header">
            <div className="chat-header-left">
              <div className="chat-header-icon" style={{ background: 'transparent', borderRadius: 0 }}>
                <RobotIcon />
              </div>
              <h1 className="chat-header-title">JozHRM Assistant</h1>
            </div>
            <div className="chat-header-actions">
              <button
                className="chat-header-action-btn"
                onClick={() => setIsDarkMode(!isDarkMode)}
                aria-label={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              <button
                className="chat-header-action-btn"
                onClick={() => setIsExpanded(!isExpanded)}
                aria-label={isExpanded ? "Minimize" : "Expand"}
              >
                {isExpanded ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 16H8V19H10V14H5V16ZM8 8H5V10H10V5H8V8ZM19 16H16V19H14V14H19V16ZM16 8H19V10H14V5H16V8Z" fill="currentColor" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 14H5V19H10V17H7V14ZM5 10H7V7H10V5H5V10ZM17 17H14V19H19V14H17V17ZM14 5V7H17V10H19V5H14Z" fill="currentColor" />
                  </svg>
                )}
              </button>
              <button
                className="chat-header-action-btn chat-close-action"
                onClick={handleClose}
                aria-label="Close"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor" />
                </svg>
              </button>
            </div>
          </header>

          {/* Messages */}
          <div className="chat-modal-body" ref={chatContainerRef}>
            {messages.length === 0 && (
              <div className="chat-welcome">
                <h3>How can I help you today?</h3>
                <div className="chat-welcome-suggestions">
                  <button className="chat-suggestion-chip" onClick={() => handleQuickAction('applicants')}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M16 11C17.66 11 18.99 9.66 18.99 8C18.99 6.34 17.66 5 16 5C14.34 5 13 6.34 13 8C13 9.66 14.34 11 16 11ZM8 11C9.66 11 10.99 9.66 10.99 8C10.99 6.34 9.66 5 8 5C6.34 5 5 6.34 5 8C5 9.66 6.34 11 8 11ZM8 13C5.67 13 1 14.17 1 16.5V19H15V16.5C15 14.17 10.33 13 8 13ZM16 13C15.71 13 15.38 13.02 15.03 13.05C16.19 13.89 17 15.02 17 16.5V19H23V16.5C23 14.17 18.33 13 16 13Z" fill="currentColor" />
                    </svg>
                    List all applicants
                  </button>
                  <button className="chat-suggestion-chip" onClick={() => handleQuickAction('schedules')}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V10H19V20ZM19 8H5V6H19V8Z" fill="currentColor" />
                    </svg>
                    Show upcoming schedules
                  </button>
                  <button className="chat-suggestion-chip" onClick={() => handleQuickAction('dashboard')}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 9.2H8V19H5V9.2ZM10.6 5H13.4V19H10.6V5ZM16.2 13H19V19H16.2V13Z" fill="currentColor" />
                    </svg>
                    Dashboard data
                  </button>
                  <button className="chat-suggestion-chip" onClick={() => handleQuickAction('jobSearches')}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="currentColor" />
                    </svg>
                    Show talent searches
                  </button>
                  <button className="chat-suggestion-chip" onClick={() => handleQuickAction('results')}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM17 17H7V15H17V17ZM17 13H7V11H17V13ZM17 9H7V7H17V9Z" fill="currentColor" />
                    </svg>
                    Show the latest 5 interview results
                  </button>
                </div>
              </div>
            )}

            {messages.map((message) => (
              <div
                key={message.id}
                className={`chat-qa-group ${message.role === 'user' ? 'chat-qa-user' : 'chat-qa-assistant'}`}
              >
                {message.role === 'assistant' && (
                  <div className="chat-avatar chat-avatar-bot" style={{ background: 'transparent', border: 'none', borderRadius: 0 }}>
                    <div style={{ width: '28px', height: '28px' }}><RobotIcon /></div>
                  </div>
                )}
                <div className={`chat-bubble ${message.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-bot'}`}>
                  {message.role === 'assistant' && !message.content ? (
                    <TypingIndicator />
                  ) : message.role === 'assistant' && message.isNew && !message.content.includes('<table') && !message.content.includes('<chart') ? (
                    <Typewriter
                      text={message.content}
                      speed={15}
                      onComplete={() => {
                        setMessages((prev) =>
                          prev.map((msg) => (msg.id === message.id ? { ...msg, isNew: false } : msg))
                        );
                      }}
                      scrollRef={chatContainerRef}
                    />
                  ) : message.role === 'assistant' ? (
                    <HtmlContent html={message.content} isExpanded={isExpanded} onExpandRequest={() => setIsExpanded(true)} />
                  ) : (
                    <span>{message.content}</span>
                  )}
                </div>
                {message.role === 'user' && (
                  <div className="chat-avatar chat-avatar-user">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="var(--le-on-surface-variant)" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Input */}
          <div className="chat-modal-input-container">
            <div className="chat-modal-input-inner">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message here..."
                rows={1}
                disabled={isLoading}
                style={{ fieldSizing: 'content' } as any}
              />
              <div className="chat-input-actions">
                <button
                  className="chat-send-btn"
                  onClick={handleSend}
                  disabled={!inputValue.trim() || isLoading}
                  aria-label="Send message"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 12L20 4L12 20L11 13L4 12Z" fill="currentColor" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="chat-disclaimer">
              JozHRM Assistant can make mistakes. Consider verifying critical information.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatOverlay;
